import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UsageAnalytics = ({ subscriptionData, currentPlan }) => {
  const [timeframe, setTimeframe] = useState('month');
  const [selectedMetric, setSelectedMetric] = useState('profile_views');

  const usageData = {
    month: {
      profile_views: {
        current: 2847,
        previous: 2156,
        limit: currentPlan === 'free' ? 1000 : 'unlimited',
        trend: 'up',
        data: [1200, 1400, 1800, 2100, 2300, 2500, 2847]
      },
      messages_sent: {
        current: 156,
        previous: 89,
        limit: currentPlan === 'free' ? 10 : 'unlimited',
        trend: 'up',
        data: [20, 35, 60, 85, 110, 135, 156]
      },
      search_queries: {
        current: 234,
        previous: 198,
        limit: 'unlimited',
        trend: 'up',
        data: [45, 78, 123, 156, 189, 210, 234]
      },
      connections_made: {
        current: 28,
        previous: 15,
        limit: 'unlimited',
        trend: 'up',
        data: [3, 8, 12, 18, 22, 25, 28]
      }
    }
  };

  const metrics = [
    {
      id: 'profile_views',
      name: 'Vues de profil',
      icon: 'Eye',
      description: 'Nombre de fois où votre profil a été consulté',
      color: 'text-primary'
    },
    {
      id: 'messages_sent',
      name: 'Messages envoyés',
      icon: 'MessageSquare',
      description: 'Nombre de messages privés envoyés',
      color: 'text-accent'
    },
    {
      id: 'search_queries',
      name: 'Recherches effectuées',
      icon: 'Search',
      description: 'Nombre de recherches dans la plateforme',
      color: 'text-success'
    },
    {
      id: 'connections_made',
      name: 'Connexions établies',
      icon: 'Users',
      description: 'Nouvelles connexions professionnelles',
      color: 'text-warning'
    }
  ];

  const timeframes = [
    { id: 'week', label: 'Cette semaine' },
    { id: 'month', label: 'Ce mois' },
    { id: 'quarter', label: 'Ce trimestre' },
    { id: 'year', label: 'Cette année' }
  ];

  const getCurrentMetricData = () => {
    return usageData?.[timeframe]?.[selectedMetric] || {};
  };

  const getSelectedMetric = () => {
    return metrics?.find(m => m?.id === selectedMetric) || metrics?.[0];
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000)?.toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000)?.toFixed(1) + 'K';
    }
    return num?.toString();
  };

  const calculateGrowth = (current, previous) => {
    if (previous === 0) return 100;
    return Math.round(((current - previous) / previous) * 100);
  };

  const getUsagePercentage = (current, limit) => {
    if (limit === 'unlimited' || !limit) return 0;
    return Math.min((current / limit) * 100, 100);
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'text-error';
    if (percentage >= 70) return 'text-warning';
    return 'text-success';
  };

  const metricData = getCurrentMetricData();
  const selectedMetricInfo = getSelectedMetric();
  const growth = calculateGrowth(metricData?.current, metricData?.previous);

  return (
    <div className="space-y-8">
      {/* Header Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">
            Analyses d'utilisation
          </h2>
          <p className="text-muted-foreground">
            Suivez votre activité et optimisez votre présence
          </p>
        </div>
        
        <div className="flex items-center space-x-3">
          <select
            value={timeframe}
            onChange={(e) => setTimeframe(e?.target?.value)}
            className="px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          >
            {timeframes?.map(tf => (
              <option key={tf?.id} value={tf?.id}>{tf?.label}</option>
            ))}
          </select>
          
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            iconPosition="left"
            iconSize={16}
            onClick={() => console.log('Export analytics')}
          >
            Exporter
          </Button>
        </div>
      </div>
      {/* Metrics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        {metrics?.map((metric) => {
          const data = usageData?.[timeframe]?.[metric?.id];
          const percentage = getUsagePercentage(data?.current, data?.limit);
          const isSelected = metric?.id === selectedMetric;
          
          return (
            <div
              key={metric?.id}
              onClick={() => setSelectedMetric(metric?.id)}
              className={`cursor-pointer bg-card rounded-lg border p-6 shadow-sm transition-all hover:shadow-md ${
                isSelected ? 'border-accent ring-2 ring-accent/20' : 'border-border'
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${
                  isSelected ? 'bg-accent/10' : 'bg-muted/50'
                }`}>
                  <Icon 
                    name={metric?.icon} 
                    size={20} 
                    className={isSelected ? 'text-accent' : metric?.color}
                  />
                </div>
                {data?.trend === 'up' && (
                  <Icon name="TrendingUp" size={16} className="text-success" />
                )}
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">
                  {metric?.name}
                </p>
                <p className="text-2xl font-bold text-card-foreground">
                  {formatNumber(data?.current || 0)}
                </p>
                
                {data?.limit !== 'unlimited' && data?.limit && (
                  <div className="space-y-1">
                    <div className="flex justify-between items-center text-xs text-muted-foreground">
                      <span>Limite : {formatNumber(data?.limit)}</span>
                      <span className={getUsageColor(percentage)}>
                        {percentage?.toFixed(0)}%
                      </span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-1.5">
                      <div 
                        className={`h-1.5 rounded-full transition-all duration-300 ${
                          percentage >= 90 ? 'bg-error' 
                          : percentage >= 70 ? 'bg-warning' :'bg-success'
                        }`}
                        style={{ width: `${Math.min(percentage, 100)}%` }}
                      />
                    </div>
                  </div>
                )}
                
                <div className="flex items-center space-x-2 text-xs">
                  <Icon name="TrendingUp" size={12} className="text-success" />
                  <span className="text-success">+{growth}%</span>
                  <span className="text-muted-foreground">vs période précédente</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {/* Detailed Chart View */}
      <div className="bg-card rounded-lg border border-border p-6 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-card-foreground flex items-center space-x-3">
              <Icon name={selectedMetricInfo?.icon} size={20} className={selectedMetricInfo?.color} />
              <span>{selectedMetricInfo?.name}</span>
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {selectedMetricInfo?.description}
            </p>
          </div>
          
          <div className="text-right">
            <p className="text-2xl font-bold text-card-foreground">
              {formatNumber(metricData?.current || 0)}
            </p>
            <div className="flex items-center space-x-2 text-sm">
              <Icon name="TrendingUp" size={14} className="text-success" />
              <span className="text-success">+{growth}%</span>
            </div>
          </div>
        </div>

        {/* Simple Chart Representation */}
        <div className="space-y-4">
          <div className="flex items-end justify-between h-40 bg-muted/30 rounded-lg p-4">
            {metricData?.data?.map((value, index) => (
              <div
                key={index}
                className="flex flex-col items-center space-y-2"
              >
                <div
                  className={`bg-accent rounded-t transition-all duration-300 w-8`}
                  style={{
                    height: `${(value / Math.max(...(metricData?.data || [1]))) * 120}px`
                  }}
                />
                <span className="text-xs text-muted-foreground">
                  J{index + 1}
                </span>
              </div>
            ))}
          </div>
          
          {/* Chart Legend */}
          <div className="flex items-center justify-center space-x-6 text-sm">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-accent rounded"></div>
              <span className="text-muted-foreground">Progression journalière</span>
            </div>
          </div>
        </div>
      </div>
      {/* Usage Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Plan Usage Summary */}
        <div className="bg-card rounded-lg border border-border p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-card-foreground mb-4">
            Résumé de votre plan
          </h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center space-x-3">
                <Icon name="Crown" size={18} className="text-accent" />
                <span className="text-sm font-medium text-card-foreground">
                  Plan actuel
                </span>
              </div>
              <span className="text-sm font-semibold text-card-foreground capitalize">
                {currentPlan}
              </span>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground">Utilisation optimale</span>
                <span className="text-card-foreground font-medium">78%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div className="bg-success h-2 rounded-full" style={{ width: '78%' }}></div>
              </div>
            </div>

            {currentPlan === 'free' && (
              <div className="bg-warning/10 border border-warning/20 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-warning">
                      Limite bientôt atteinte
                    </p>
                    <p className="text-xs text-warning/80 mt-1">
                      Vous avez utilisé {Math.round(getUsagePercentage(subscriptionData?.usage?.messagesSent, 10))}% de votre limite de messages.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Recommendations */}
        <div className="bg-card rounded-lg border border-border p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-card-foreground mb-4">
            Recommandations
          </h3>
          
          <div className="space-y-4">
            <div className="flex items-start space-x-3 p-3 bg-primary/5 rounded-lg">
              <Icon name="TrendingUp" size={16} className="text-primary mt-0.5" />
              <div>
                <p className="text-sm font-medium text-card-foreground">
                  Activité en hausse
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Vos vues de profil ont augmenté de {growth}% ce mois-ci. 
                  Continuez à publier du contenu régulièrement.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3 p-3 bg-accent/5 rounded-lg">
              <Icon name="MessageSquare" size={16} className="text-accent mt-0.5" />
              <div>
                <p className="text-sm font-medium text-card-foreground">
                  Engagez plus
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Répondez plus rapidement aux messages pour améliorer votre taux de réponse.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3 p-3 bg-success/5 rounded-lg">
              <Icon name="Users" size={16} className="text-success mt-0.5" />
              <div>
                <p className="text-sm font-medium text-card-foreground">
                  Réseau en croissance
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Vous avez établi {metricData?.current || 28} nouvelles connexions. 
                  Objectif : 35 d'ici la fin du mois.
                </p>
              </div>
            </div>

            {currentPlan !== 'enterprise' && (
              <div className="pt-4">
                <Button
                  variant="outline"
                  size="sm"
                  fullWidth
                  iconName="ArrowUp"
                  iconPosition="left"
                  iconSize={14}
                  onClick={() => console.log('Upgrade plan')}
                >
                  Upgrader pour plus d'insights
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Export Options */}
      <div className="bg-muted/30 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Exporter vos données
            </h3>
            <p className="text-sm text-muted-foreground">
              Téléchargez vos analytics pour une analyse approfondie
            </p>
          </div>
          
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              size="sm"
              iconName="FileSpreadsheet"
              iconPosition="left"
              iconSize={16}
              onClick={() => console.log('Export CSV')}
            >
              CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="FileText"
              iconPosition="left"
              iconSize={16}
              onClick={() => console.log('Export PDF')}
            >
              PDF
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsageAnalytics;